import React from 'react';
import { motion } from 'framer-motion';
import { BotIcon, PhoneCallIcon, FileTextIcon, ClipboardListIcon, ShoppingBagIcon, UserRoundIcon } from 'lucide-react';
import { SectionIntro } from '../layout/SectionIntro';
const aiLane = ['Keyword & market research', 'Technical audits', 'Content planning', 'Competitor review', 'QA & regression checks'];
const humanLane = ['Strategy & priorities', 'Final copy in your voice', 'Design decisions', 'Implementation', 'What gets published'];
const actions = [{
  label: 'Calls',
  icon: PhoneCallIcon
}, {
  label: 'Form leads',
  icon: FileTextIcon
}, {
  label: 'Quote requests',
  icon: ClipboardListIcon
}, {
  label: 'Orders',
  icon: ShoppingBagIcon
}];
export function AiSeoLanes() {
  return <section id="ai-seo" className="relative overflow-hidden bg-navy-900 py-16 lg:py-24" data-id="element-16" data-mp="22u6mil0kyhbo">
      <div className="absolute inset-0 bp opacity-60" aria-hidden="true" data-id="element-17" data-mp="1f4uc5htio37s" />
      <div className="absolute -left-32 bottom-0 h-[30rem] w-[30rem] rounded-full bg-forge-cyan/[0.08] blur-[130px]" aria-hidden="true" data-id="element-18" data-mp="1a7u8teo1xekd" />

      <div className="relative mx-auto max-w-8xl px-5 sm:px-8 lg:px-10" data-id="element-19" data-mp="2u6m6jakybyh">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end" data-id="element-20" data-mp="7shx6xcm2sia">
          <SectionIntro className="lg:col-span-7" eyebrow="Human-led, AI-accelerated" title={<>
                We use AI to move faster.
                <span className="block text-forge-cyan" data-id="element-22" data-mp="3g18c3u68r8sp">Experience decides what matters.</span>
              </>} body="AI covers ground no team could cover by hand: research, audits, planning, and QA. It does not set your strategy, write your final copy, or decide what ships. We do that — and we can tell you exactly what changed and why." data-id="element-21" data-mp="cki2z6fd4fms" />
          <div className="lg:col-span-5" data-id="element-23" data-mp="3tyn6zef9k8s2">
            <div className="rounded-2xl border border-white/10 bg-navy-950/70 p-5" data-id="element-24" data-mp="159gp0emlw1xd">
              <p className="text-[13px] font-bold text-white" data-id="element-25" data-mp="19a8xh5nnm2sl">What we will not tell you</p>
              <ul className="mt-3 space-y-2.5 text-[13px] leading-relaxed text-mist-300" data-id="element-26" data-mp="36b4y3iha17ty">
                <li className="flex gap-2.5" data-id="element-27" data-mp="36czj0wsjszy1">
                  <span className="mt-[7px] h-1 w-1 shrink-0 rotate-45 bg-forge-orange" aria-hidden="true" data-id="element-28" data-mp="31b0jjxzxin6d" />
                  That we can guarantee a #1 ranking. Nobody can, and we will not sell it.
                </li>
                <li className="flex gap-2.5" data-id="element-29" data-mp="3ad9tomracej8">
                  <span className="mt-[7px] h-1 w-1 shrink-0 rotate-45 bg-forge-orange" aria-hidden="true" data-id="element-30" data-mp="2mmw5x8fqh1ug" />
                  That AI writes your site. Your brand deserves a person reading it last.
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* two lanes merging */}
        <div className="relative mt-14" data-id="element-31" data-mp="2rm0epxyqwxk9">
          <div className="grid gap-4 lg:grid-cols-12 lg:gap-0" data-id="element-32" data-mp="2rkxqxywpzb8k">
            <div className="lg:col-span-8" data-id="element-33" data-mp="23vmlw58c6uom">
              <Lane title="AI-accelerated" caption="Speed and coverage" icon={BotIcon} items={aiLane} accent="cyan" data-id="element-34" data-mp="23ulce56yyg90" />
              <div className="mt-4 lg:mt-5" data-id="element-35" data-mp="j692axxvmfhv">
                <Lane title="Human-led" caption="Judgement and accountability" icon={UserRoundIcon} items={humanLane} accent="orange" data-id="element-36" data-mp="j7anvwciqkdu" />
              </div>
            </div>

            {/* merge */}
            <div className="relative lg:col-span-4" data-id="element-37" data-mp="y4upque4bzfs">
              <svg viewBox="0 0 120 260" preserveAspectRatio="none" className="pointer-events-none absolute -left-3 top-0 hidden h-full w-16 lg:block" aria-hidden="true" data-id="element-38" data-mp="124v4yh91kenw">
                <path d="M0 62 C 40 62, 45 130, 90 130" fill="none" stroke="#2BB6EE" strokeWidth="1.4" vectorEffect="non-scaling-stroke" className="flowline" data-id="element-39" data-mp="t2vlzjegv0ib" />
                <path d="M0 198 C 40 198, 45 130, 90 130" fill="none" stroke="#FF6B18" strokeWidth="1.4" vectorEffect="non-scaling-stroke" className="flowline" data-id="element-40" data-mp="3t5ciftasohaw" />
              </svg>

              <motion.div initial={{
              opacity: 0,
              x: 18
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true,
              amount: 0.3
            }} transition={{
              duration: 0.45
            }} className="relative h-full rounded-2xl border border-forge-green/25 bg-forge-green/[0.06] p-6 lg:ml-12" data-id="element-41" data-mp="3fe7fah87x6vt">
                <p className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-forge-green" data-id="element-42" data-mp="1yj9225ulbva">Both lanes report here</p>
                <h3 className="mt-3 font-display text-[1.15rem] font-extrabold leading-snug text-white" data-id="element-43" data-mp="2rod381vq3mcm">
                  SEO measured in actions, not screenshots of rankings.
                </h3>
                <ul className="mt-5 space-y-2.5" data-id="element-44" data-mp="2cld1f83ngtgg">
                  {actions.map(a => <li key={a.label} className="flex items-center gap-2.5 rounded-xl bg-navy-950/60 px-3.5 py-2.5 ring-1 ring-inset ring-white/[0.08]" data-id="element-45" data-mp="2hiljz9spmzi8">
                      <a.icon className="h-4 w-4 shrink-0 text-forge-green" aria-hidden="true" data-id="element-46" data-mp="2r9zq40f62xkc" />
                      <span className="text-[12.5px] font-semibold text-mist-100" data-id="element-47" data-mp="2va079stnhmv3">{a.label}</span>
                    </li>)}
                </ul>
                <p className="mt-5 border-t border-white/10 pt-4 text-[12px] leading-relaxed text-mist-300" data-id="element-48" data-mp="3598zoh8vatfc">
                  We report on what the site produced, what we changed, and what we are doing next month.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
type LaneProps = {
  title: string;
  caption: string;
  items: string[];
  accent: 'cyan' | 'orange';
  icon: React.ComponentType<{
    className?: string;
  }>;
};
function Lane({
  title,
  caption,
  items,
  accent,
  icon: Icon
}: LaneProps) {
  const cyan = accent === 'cyan';
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.3
  }} transition={{
    duration: 0.45
  }} className="relative overflow-hidden rounded-2xl border border-white/10 bg-navy-950/70 p-5 sm:p-6" data-id="element-49" data-mp="1hs2joo6c4zmw">
      <span className={`absolute inset-y-0 left-0 w-[3px] ${cyan ? 'bg-forge-cyan' : 'bg-forge-orange'}`} aria-hidden="true" data-id="element-50" data-mp="1ltect3of42zu" />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3" data-id="element-51" data-mp="20gzcenm33k30">
        <div className="flex items-center gap-3" data-id="element-52" data-mp="1vj7n81ctqc2i">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-white/[0.06] ring-1 ring-inset ring-white/10" data-id="element-53" data-mp="1vjfw7i5tsx8f">
            <Icon className={`h-5 w-5 ${cyan ? 'text-forge-cyan' : 'text-forge-orange'}`} data-id="element-54" data-mp="29gcn1jly2epb" />
          </span>
          <div data-id="element-55" data-mp="2o5ebwjoaa4is">
            <h3 className="font-display text-[15px] font-bold text-white" data-id="element-56" data-mp="2o4w7gzrgwjzs">{title}</h3>
            <p className="text-[10.5px] uppercase tracking-[0.14em] text-mist-400" data-id="element-57" data-mp="2t4ihz1fwvm4w">{caption}</p>
          </div>
        </div>

        <ul className="flex flex-1 flex-wrap items-center gap-2" data-id="element-58" data-mp="2f5iwg2np3ncy">
          {items.map((item, i) => <li key={item} className="flex items-center gap-2" data-id="element-59" data-mp="2k314zrxsw0kf" data-mpi={i}>
              <span className="rounded-lg bg-white/[0.05] px-3 py-2 text-[12px] font-medium text-mist-100" data-id="element-60" data-mp="25ewomum8014t">{item}</span>
              {i !== items.length - 1 && <span className={`hidden h-px w-3 sm:block ${cyan ? 'bg-forge-cyan/40' : 'bg-forge-orange/40'}`} aria-hidden="true" data-id="element-61" data-mp="2a6xv1zwpixmx" />}
            </li>)}
        </ul>
      </div>
    </motion.div>;
}